/* eslint-disable @nx/enforce-module-boundaries */
'use client';
import { Button, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal, Scrollable } from '@mybridge/ui';
import { Card } from '@mybridge/ui/card';
import { Box, Container, HStack, Stack } from '@mybridge/ui/layout';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import ProfileStickyHeader from 'v4/components/profile-sticky-header';
import { AudiencePermissionModal } from 'v4/components/profile/audience-modal';
import { MyProfileAbout } from 'v4/snippets/profile/about';
import { MyProfileCertificateModal } from 'v4/components/profile/certificates-modal';
import {
  MyProfileContext,
  useMyProfileContext,
} from 'v4/components/profile/context';
import { MyProfileEducationModal } from 'v4/components/profile/education-modal';
import { MyProfileExperienceModal } from 'v4/components/profile/experience-modal';
import { MyProfileIntroductionModal } from 'v4/components/profile/introduction-modal';
import { MyProfileMilestoneModal } from 'v4/components/profile/milestones-modal';
import { MyProfileUpdateModal } from 'v4/components/profile/profile-modal';
import { MyProfileSkillsModal } from 'v4/components/profile/skills-modal';
import { MyProfileNetworkSuggestionsCarousel } from 'v4/components/public-profile/suggestions-carousel';
import Banner from 'v4/snippets/profile/banner';
import ProfileTabs from 'v4/snippets/profile/tabs';
import { getAllConnectionIDs } from 'v4/store/actions/connection.actions';
import { MyProfileMyBioDropdown } from '../my-bio-dropdown';
import { getUserInfoPDF } from 'v4/store/actions/user-profile.actions';
import Head from 'next/head';
import { DashboardPostForm } from 'v4/snippets/dashboard/post-form';
import { DashboardFeed } from 'v4/snippets/dashboard/feed';
import { SiteShortcutLinks } from 'v4/snippets/dashboard/site-shortcut-links';
import { DashboardMyCircle } from 'v4/snippets/dashboard/circle';
import { MyProfileIntroduction } from 'v4/components/public-profile/introduction';
import { MyProfileEditButtonsGroup } from 'v4/components/profile/edit-btn-group';
import { MyProfileViewsCount } from 'v4/components/profile/views-count';
import { MyProfileLink } from 'v4/components/profile/profile-link';
import { MyProfileVisibility } from 'v4/components/profile/visibility';
import { MyProfileAnalytics } from '../analytics';
import { MyConnectionsBox } from 'v4/components/profile/my-connections';
import { Divider } from '@chakra-ui/react';
import styles from '../banner/banner.module.scss'
import statusProfileStyles from 'v4/components/header/status-dropdown/statusProfile.module.scss';
import { HorizontalThreeDot } from '@mybridge/icons';
import { useRouter } from 'next/router';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { getUserFullName } from 'v4/lib/commons';

export const ProfileLayout = ({ children, active, noBanner = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslator();
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const userName = getUserFullName(userProfileInfo);
  const { loggedIn, pageLoaded } = useSelector((state) => state.user);
  const { globalNotifCount } = useSelector((state) => state.notifications);
  const router = useRouter();
  // const isProfile=router.pathname==='/profile/me'
  const isProfile=router.pathname==='/profile/me'||router.pathname==='/profile/about'||router.pathname==='/profile/photos'||router.pathname==='/profile/videos'
  const direction = useDirection();
  const [stickyActive, setStickyActive] = useState(false);
  useEffect(() => {
    dispatch(getAllConnectionIDs());
  }, []);
  const myProfileContext = useMyProfileContext({});
  const {
    introductionDisc,
    experienceDisc,
    profileDisc,
    skillsDisc,
    educationDisc,
    milestonesDisc,
    certificatesDisc,
    audienceDisc,
    jobPreferences,
    peopleSliderDisc,
  } = myProfileContext ?? {};
  const { profile_intro: intro, profile_headline: headline } =
    userProfileInfo ?? {};

  // const requestProfilePDF = () => {
  //   dispatch(getUserInfoPDF()).then((res) => {
  //     const url = res?.payload;
  //     // Create a temporary <a> element to trigger the download
  //     const tempLink = document.createElement("a");
  //     tempLink.href = url;
  //     tempLink.setAttribute(
  //       "download",
  //       `${userProfileInfo?.first_name}_${userProfileInfo?.last_name}_profile.pdf`
  //     ); // Set the desired filename for the downloaded file

  //     // Append the <a> element to the body and click it to trigger the download
  //     document.body.appendChild(tempLink);
  //     tempLink.click();

  //     // Clean up the temporary elements and URL
  //     document.body.removeChild(tempLink);
  //     window.URL.revokeObjectURL(url);
  //   });
  // };

  const requestProfilePDF = async () => {
      try {
        const res = await dispatch(getUserInfoPDF(userProfileInfo?.slug));
        const htmlContent = res?.payload;
  
        if (!htmlContent) {
          alert('Failed to get profile HTML from server.');
          return;
        }
  
        // Create hidden iframe
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.left = '0';
        iframe.style.top = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.opacity = '0';
        document.body.appendChild(iframe);
  
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(htmlContent); // Use the HTML exactly as returned from backend
        doc.close();
  
        // Wait for everything to load before printing
        await new Promise(resolve => {
          const check = () => {
            if (doc.readyState === 'complete') resolve();
          };
          iframe.onload = check;
          check();
        });
  
        if (doc.fonts && doc.fonts.ready) {
          await doc.fonts.ready;
        }
  
        const imgs = Array.from(doc.images || []);
        await Promise.all(
          imgs.map(img =>
            img.complete ? Promise.resolve() : new Promise(r => { img.onload = img.onerror = r; })
          )
        );
  
        // Trigger the native print dialog
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
  
        // Clean up
        setTimeout(() => iframe.remove(), 1000);
  
      } catch (err) {
        console.error(err);
        alert('Error generating PDF.');
      }
    };

  const pageTitle = [
    loggedIn && globalNotifCount ? `(${globalNotifCount})` : null,
    loggedIn && userProfileInfo
      ? `${userProfileInfo?.first_name} ${userProfileInfo?.last_name}`
      : 'Profile',
    'myBridge.me'
  ].filter(Boolean).join(' | ');

  return (
    <>
      <MyProfileContext.Provider value={myProfileContext}>
        <Head>
          <title>{pageTitle}</title>
        </Head>
        <HStack
          flexDir={['column', 'column', 'row']}
          alignItems="flex-start"
          justifyContent="center"
          // spacing={0}
          overFlow={isMobile&&"hidden"}
          w="100%"
          backgroundColor="rgb(255, 255, 255)"

        >
          {/* Left */}
          {!isMobile && (
            /* <Stack
              display={['none', 'none', 'flex']}
              // className={styles.left}
              // w={['90px', '90px', '280px']}
              pos="sticky"
              top="64px"
              spacing={2}
              // bg="white"
              // boxShadow="2px 0px 6px 0px #00000033"
              //minH="100vh"
              borderBottom="1px solid #e0e0e094"
              height={'500px'}
              sx={{
                '@media (max-width: 1301px)': {
                  width: '80px',
                },
                '@media (max-width: 1182px)': {
                  width: '85px',
                },
              }}

            // overflowX={"hidden"}
            // overflowy={"auto"}
            > */
            <Stack
              display={['none', 'none', 'flex']}
              className={statusProfileStyles.left}
              pos="sticky"
              top="64px"
              spacing={2}
              borderBottom="1px solid #e0e0e094"
              height={'500px'}
            >
              {/* <Scrollable maxH="99vh" flex={1}> */}

              <Stack
                spacing={2}
                // w={['200px', '20px', '20px']}
                justifyContent="space-between"
                height="100%"
                align="stretch"
                h="calc(100vh - 0px)"
                // className={styles.paddingLeftSmall}
                backgroundColor="rgb(255, 255, 255)"
              // overflowY={"auto"}
              // overflowX={"hidden"}
              >
                <Stack spacing={2} align="stretch" h="100%" flex="1">
                  <SiteShortcutLinks />

                  <Box w="230px" className={styles.Network}>
                    <DashboardMyCircle h="300px" flex="1" />
                  </Box>
                </Stack>
                {/* <ImportContacts /> */}
                {/* <MainCopyrightsText /> */}
              </Stack>
              {/* </Scrollable> */}
            </Stack>
          )}

          {/* center */}
          {(
            <Box width={isMobile&&isProfile?'100vw':""} className={styles.centerZoom}>
              <Stack spacing={4} mt={2}>
                {noBanner ? (
                  ''
              ) : (
                  <>
                    <Card
                      variant="thinBorderedCard"
                      borderRadius="0"
                      position="relative"
                    >
                      {(
                        <Box
                          bgImage={
                            userProfileInfo?.profile_banner_pic &&
                              !userProfileInfo?.profile_banner_pic.includes(
                                'banner-placeholder1.png'
                              )
                              ? userProfileInfo?.profile_banner_pic
                              : '/images/defaultBanner.svg'
                          }
                         
                        ></Box>
                      )}

                      <Banner />
                      <Container w={isMobile?'100vw':"100%"} maxW="none" p={0} zIndex="1" {...(direction === 'rtl') ? {mr: 1} : {ml: 1}}>
                        {peopleSliderDisc?.isOpen && (
                          <MyProfileNetworkSuggestionsCarousel isFromDashbord={true}/>
                        )}

                        {!stickyActive && (
                          <ProfileTabs
                            active={active}
                            borderTop="1px solid #EFF3F4"
                            mt={3}
                          />
                        )}
                      </Container>
                    </Card>
                    {!isMobile && <ProfileStickyHeader
                      profile={userProfileInfo}
                      tabsMenu={<ProfileTabs active={active}
                      
                      
                      />}
                      fromPrivate={true}
                      mt={-4}
                      isActive={(e) => setStickyActive(e)}
                      actions={
                        <Menu >
                          <MenuButton
                            as={IconButton}
                            icon={<HorizontalThreeDot />}
                            sx={{
                              borderRadius: '27px ',
                              border: '1px solid #00000033',
                              backgroundColor: 'transparent',
                              color: 'inherit',
                              _hover: {
                                backgroundColor: '#E4E6EB',
                                color: 'inherit',
                              },
                              _focus: {
                                backgroundColor: 'white',
                                color: 'inherit',
                              },
                            }}
                          />
                          <Portal >
                            <MenuList zIndex={99999}>
                              <MenuItem
                                onClick={() =>
                                  router.push('/p/' + userProfileInfo?.slug)
                                }
                              >
                                {t('myCalendar.viewAsPublic') || "View as public"}
                              </MenuItem>
                              <MenuItem onClick={() => requestProfilePDF()}>
                                {t('profile.saveToPDF') || "Save to PDF"}
                              </MenuItem>
                              <MyProfileMyBioDropdown fromPrivate={true} userName={userName} jobPreferences={jobPreferences} />
                            </MenuList>
                          </Portal>
                        </Menu>
                      }
                    />}
                  </>
                )}
                <Container maxW="1270px" p={(isMobile && router.pathname!=='/profile/me' )?3:0}>
                  {children}
                </Container>
              </Stack>
            </Box>

          )}

          {/* Right */}
          {!isMobile && (
            <Scrollable
              // className={styles.rightZoomPannel}
              // width="50%"
              // minW="360px"
              pos="sticky"
              //top="2rem"
              top="76px"
              //maxH="100vh"
              maxH="85vh"
              sx={{
                '@media (max-width: 1024px)': {
                  display: 'none'
                },
              }}
            >
              <Stack
                spacing={0}
                // w={['100%', '300px', '350px']}
                borderTopRightRadius="10px"
                borderTopLeftRadius="10px"
                // pt={1}
                className={styles.rightZoom}
              >
                {isMobile ? (
                  <Card pt={6} p={4} variant="thinBorderedCard" >
                    <Stack spacing={3} m={3} mt={0}>
                      {intro?.replace(/<\/?[^>]+(>|$)/g, "")?.length && (
                        <Card variant="thinBorderedCard">
                          <MyProfileIntroduction displayCard />
                        </Card>
                      )}

                      <MyProfileEditButtonsGroup />
                      <Card variant="thinBorderedCard">
                        <MyProfileViewsCount />
                      </Card>
                      <Card border="1px solid #e0e0e094" variant="thinBorderedCard">
                        <MyProfileLink />
                      </Card>
                      <Card border="1px solid #e0e0e094" variant="thinBorderedCard">
                        <MyProfileVisibility />
                      </Card>
                      <Card border="1px solid #e0e0e094" variant="thinBorderedCard">
                        <MyProfileAnalytics />
                      </Card>
                      <Box p={3}>
                        <MyConnectionsBox limit={2} orientation="table" seeAll />
                      </Box>
                    </Stack>

                  </Card>
                ) : (

                  <Stack>
                    {intro?.replace(/<\/?[^>]+(>|$)/g, "")?.length > 0 && (
                      <Card p={3} variant="thinBorderedCard">
                        <MyProfileIntroduction displayCard />
                      </Card>
                    )}

                    <MyProfileEditButtonsGroup />
                    <Card borderRadius="16px" p={3} border="1px solid #e0e0e094" w='330px' variant="thinBorderedCard">
                      <MyProfileViewsCount />
                    </Card>
                    <Card borderRadius="16px" p={3} border="1px solid #e0e0e094" w='330px' variant="thinBorderedCard">
                      <MyProfileLink />
                    </Card>
                    <Card borderRadius="16px" p={3} border="1px solid #e0e0e094" w='330px' variant="thinBorderedCard">
                      <MyProfileVisibility />
                    </Card>
                    <Card borderRadius="16px" p={3} border="1px solid #e0e0e094" w='330px' variant="thinBorderedCard">
                      <MyProfileAnalytics />
                    </Card>
                    <Box mb={"30px"} p="2px">
                      <MyConnectionsBox border="1px solid #e0e0e094" limit={2} orientation="table" seeAll />
                    </Box>
                  </Stack>
                )}

                {/* <DashboardFeed location={location} /> */}
              </Stack>
            </Scrollable>

          )}

        </HStack>
        {/* All profile modification related modals */}
        {/* {introductionDisc?.isOpen ? <MyProfileIntroductionModal /> : ''} */}
        {audienceDisc?.isOpen ? <AudiencePermissionModal /> : ''}
        {/* {experienceDisc?.isOpen ? <MyProfileExperienceModal /> : ''} */}
        {profileDisc?.isOpen ? <MyProfileUpdateModal /> : ''}
        {/* {skillsDisc?.isOpen ? <MyProfileSkillsModal /> : ''} */}
        {/* {educationDisc?.isOpen ? <MyProfileEducationModal /> : ''} */}
        {/* {milestonesDisc?.isOpen ? <MyProfileMilestoneModal /> : ''} */}
        {/* {certificatesDisc?.isOpen ? <MyProfileCertificateModal /> : ''} */}
      </MyProfileContext.Provider>

    </>
  );
};


// <MyProfileContext.Provider value={myProfileContext}>
// <Head>
//   <title>
//     {loggedIn && globalNotifCount ? `(${globalNotifCount})` : ''}{' '}
//     {userProfileInfo
//       ? `${userProfileInfo?.first_name} ${userProfileInfo?.last_name}`
//       : 'Profile'}{' '}
//     | myBridge.me
//   </title>
// </Head>
// <Stack spacing={4} mt={10}>
//   {noBanner ? (
//     ''
//   ) : (
//     <>
//       <Card
//         variant="thinBorderedCard"
//         borderRadius="0"
//         position="relative"
//       >
//         {!isMobile && (
//           <Box
//             bgImage={
//               userProfileInfo?.profile_banner_pic &&
//                 !userProfileInfo?.profile_banner_pic.includes(
//                   'banner-placeholder1.png'
//                 )
//                 ? userProfileInfo?.profile_banner_pic
//                 : '/images/defaultBanner.svg'
//             }
//             className="bannerBGContainer 1"
//           ></Box>
//         )}

//         <Banner />
//         <Container w="1220px" p={0} zIndex="1">
//           {peopleSliderDisc?.isOpen && (
//             <MyProfileNetworkSuggestionsCarousel />
//           )}

//           {!stickyActive && (
//             <ProfileTabs
//               active={active}
//               borderTop="1px solid #E4E6EB"
//               mt={3}
//             />
//           )}
//         </Container>
//       </Card>
//       {!isMobile && <ProfileStickyHeader
//         profile={userProfileInfo}
//         tabsMenu={<ProfileTabs active={active} />}
//         mt={-4}
//         isActive={(e) => setStickyActive(e)}
//         actions={
//           <HStack gap={3}>
//             <Button
//               as={Link}
//               href={'/p/' + userProfileInfo?.slug}
//               variant="secondary"
//             >
//               View as Public
//             </Button>
//             <Button
//               onClick={() => requestProfilePDF()}
//               variant="secondary"
//             >
//               Save as PDF
//             </Button>
//             <MyProfileMyBioDropdown jobPreferences={jobPreferences} />
//           </HStack>
//         }
//       />}

//     </>
//   )}
//   <Container maxW="1220px" p={0}>
//     {children}
//   </Container>
// </Stack>

// {/* All profile modification related modals */}
// {/* {introductionDisc?.isOpen ? <MyProfileIntroductionModal /> : ''} */}
// {audienceDisc?.isOpen ? <AudiencePermissionModal /> : ''}
// {/* {experienceDisc?.isOpen ? <MyProfileExperienceModal /> : ''} */}
// {profileDisc?.isOpen ? <MyProfileUpdateModal /> : ''}
// {/* {skillsDisc?.isOpen ? <MyProfileSkillsModal /> : ''} */}
// {/* {educationDisc?.isOpen ? <MyProfileEducationModal /> : ''} */}
// {/* {milestonesDisc?.isOpen ? <MyProfileMilestoneModal /> : ''} */}
// {/* {certificatesDisc?.isOpen ? <MyProfileCertificateModal /> : ''} */}
// </MyProfileContext.Provider>